import emptyModal from './emptyModalComponent.vue'
import { h, render, warn, getCurrentInstance, ref, computed } from 'vue'
import type {
  Component,
  ComponentInternalInstance,
  MaybeRefOrGetter,
  Ref,
  VNode
} from 'vue'

// Styled modal
// will get overwritten if slot is used
export interface IDefaultSlotInputs {
  headerText?: string // header slot
  contentText?: string // content slot
  footerButtonLabel?: MaybeRefOrGetter<string> // footer slot
}

export type SlotNames =
  | 'outer-modal-replacement'
  | 'inner-modal-replacement'
  | 'header'
  | 'content'
  | 'footer'
export type SlotInput = Partial<Record<SlotNames, ISlotComponent>>
type SlotProps = Record<string, unknown>
type SlotChildren = Record<string, unknown>

export interface ISlotComponent {
  component: Component
  props?: SlotProps
  innerSlots?: SlotChildren
}

export interface IComponentEventCallback {
  confirmFn?(): void
  cancelFn?(): void
  closeFn?(): void
  secondaryFn?(): void
  tertiaryFn?(): void
}

export type ModalPosition = 'center' | 'bottom-right'

export interface IModalOptions {
  showHeader?: boolean
  showBody?: boolean
  showFooter?: boolean
  showPrimaryCTA?: MaybeRefOrGetter<boolean>
  showCloseButton?: boolean
  blurBackground?: boolean
  closeOnOutsideClick?: boolean
  closeOnConfirm?: boolean
  closeOnLoadEnd?: boolean
  showBorderHeader?: boolean
  addContentPadding?: boolean
  showSecondaryCTA?: MaybeRefOrGetter<boolean>
  showTertiaryCTA?: MaybeRefOrGetter<boolean>
  footerButtonLoading?: MaybeRefOrGetter<boolean>
  secondaryButtonLoading?: MaybeRefOrGetter<boolean>
  tertiaryButtonLoading?: MaybeRefOrGetter<boolean>
  footerStyle?: string
  secondaryCTALabel?: MaybeRefOrGetter<string>
  tertiaryCTALabel?: MaybeRefOrGetter<string>
  callOnce?: boolean
  extendMobile?: boolean
  innerWidth?: string
  dialogClass?: string
  transparentBackdrop?: boolean
  position?: ModalPosition
}

export interface IModalNode {
  vNode: VNode
  el: HTMLElement
}

export interface IUniversalModal {
  modalName?: string
  default?: IDefaultSlotInputs
  slot?: SlotInput
  config?: IModalOptions
  callback?: IComponentEventCallback
}

/* eslint-disable no-unused-vars */
export interface IModal {
  showReplacementModal(
    replacement: ISlotComponent,
    onDestroy?: () => void
  ): void
  generateModal(modal: IUniversalModal): HTMLElement
  showSelectedModal(modalName: string): void
  createSlot(
    slotName: SlotNames,
    slotComponent: Component,
    slotProps?: SlotProps,
    innerSlots?: SlotChildren
  ): Partial<Record<SlotNames, ISlotComponent>>
  createSlotGeneric(
    slotComponent: Component,
    slotProps?: SlotProps,
    innerSlots?: SlotChildren
  ): ISlotComponent
  HTMLtoComponent(HTMLString: string, tag?: string): Component
  closeModal(el: HTMLElement): void
  closeActiveModal(): void
  deleteSelectedModal(modalName: string): void
  hasActiveModal: Ref<boolean>
  createComponent(
    component: Component,
    props?: SlotProps,
    slots?: SlotChildren
  ): VNode
}

// useModal composable
export function useModals(): IModal {
  // to use for each vNode
  const app = getCurrentInstance()

  return _useModals(app)
}

const activeModal = ref<IModalNode | null>(null)
const modalList = ref<Map<string, IModalNode>>(new Map<string, IModalNode>())

function _useModals(appInstance?: ComponentInternalInstance | null): IModal {
  const hasActiveModal = computed(() => !!activeModal.value)

  // create slot
  function createSlot(
    slotName: SlotNames,
    slotComponent: Component,
    slotProps?: SlotProps,
    innerSlots?: SlotChildren
  ): Partial<Record<SlotNames, ISlotComponent>> {
    const slot: Partial<Record<SlotNames, ISlotComponent>> = {
      [slotName]: {
        component: slotComponent,
        props: { ...slotProps },
        innerSlots
      }
    }
    return slot
  }

  // create slot
  function createSlotGeneric(
    slotComponent: Component,
    slotProps?: SlotProps,
    innerSlots?: SlotChildren
  ): ISlotComponent {
    return { component: slotComponent, props: { ...slotProps }, innerSlots }
  }

  // create modal object
  function generateModal(modal: IUniversalModal): HTMLElement {
    if (activeModal.value != null && modal.config?.callOnce) {
      return activeModal.value.el
    }

    let destroyVNode = {
      onClose: () => {
        destroy()
      }
    }

    let el = document.createElement('div')

    const destroy = () => {
      // run
      if (modal.callback?.closeFn) modal.callback.closeFn()
      render(null, el)

      if (activeModal.value != null) {
        activeModal.value = null
      }
    }

    let vNode = h(
      emptyModal,
      {
        ...modal.config,
        ...modal.default,
        ...destroyVNode,
        ...{
          onConfirm: () => {
            if (modal.callback?.confirmFn) modal.callback.confirmFn()
          }
        },
        ...{
          onSecondary: () => {
            if (modal.callback?.secondaryFn) modal.callback.secondaryFn()
          }
        },
        ...{
          onTertiary: () => {
            if (modal.callback?.tertiaryFn) modal.callback.tertiaryFn()
          }
        },
        ...{
          onCancel: () => {
            if (modal.callback?.cancelFn) modal.callback.cancelFn()
          }
        }
      },
      {
        'outer-modal-replacement': () => {
          if (modal.slot?.['outer-modal-replacement']?.component) {
            return h(
              modal.slot?.['outer-modal-replacement'].component,
              {
                ...modal.slot?.['outer-modal-replacement']?.props,
                ...destroyVNode,
                ...{ ref: 'modalRef' }
              },
              modal.slot['outer-modal-replacement'].innerSlots
            )
          }
        },
        'inner-modal-replacement': () => {
          if (modal.slot?.['inner-modal-replacement']?.component) {
            return h(
              modal.slot?.['inner-modal-replacement'].component,
              {
                ...modal.slot?.['inner-modal-replacement']?.props,
                ...destroyVNode,
                ...{ ref: 'modalRef' }
              },
              modal.slot['inner-modal-replacement'].innerSlots
            )
          }
        },
        header: () => {
          if (modal.slot?.['header']?.component) {
            return h(
              modal.slot?.['header'].component,
              { ...modal.slot?.header?.props, ...destroyVNode },
              modal.slot.header.innerSlots
            )
          }
        },
        content: () => {
          if (modal.slot?.['content']?.component) {
            return h(
              modal.slot?.['content'].component,
              { ...modal.slot?.content?.props, ...destroyVNode },
              modal.slot.content.innerSlots
            )
          }
        },
        footer: () => {
          if (modal.slot?.['footer']?.component) {
            return h(
              modal.slot?.['footer'].component,
              { ...modal.slot?.footer?.props, ...destroyVNode },
              modal.slot.footer.innerSlots
            )
          }
        }
      }
    )

    if (appInstance) {
      vNode.appContext = appInstance.appContext
    }

    if (modal.modalName) {
      modalList.value.set(modal.modalName, { vNode, el })
    }

    activeModal.value = { vNode, el }
    render(vNode, el)

    return el
  }

  function showSelectedModal(modalName: string) {
    if (modalList.value.has(modalName)) {
      const modalVNode = modalList.value.get(modalName)?.vNode
      const htmlEL = modalList.value.get(modalName)?.el

      if (modalVNode) {
        activeModal.value = {
          vNode: modalVNode,
          el: htmlEL ?? document.createElement('div')
        }
      }

      render(modalVNode ?? null, htmlEL ?? document.createElement('div'))
    } else {
      warn(`Could not find the specified modal named '${modalName}'.`)
    }
  }

  function deleteSelectedModal(modalName: string) {
    if (modalList.value.has(modalName)) {
      modalList.value.delete(modalName)
    } else {
      warn(`Could not find the specified modal named '${modalName}'.`)
    }
  }

  // todo: not finished
  function showReplacementModal(
    replacement: ISlotComponent,
    onDestroy?: () => void
  ) {
    let destroyVNode = {
      onClose: () => {
        destroy()
      }
    }

    if (onDestroy) {
      destroyVNode = {
        onClose: () => {
          onDestroy()
        }
      }
    }

    let vNode = h(
      emptyModal,
      { ...destroyVNode },
      {
        'inner-modal-replacement': () => {
          return h(replacement.component, { ref: 'modalRef' })
        }
      }
    )

    let el: HTMLElement | null

    render(vNode, (el = document.createElement('div')))

    const destroy = () => {
      if (el) render(null, el)
    }
  }

  function HTMLtoComponent(HTMLString: string, tag?: string) {
    if (tag) {
      return h(tag, { innerHTML: HTMLString })
    } else {
      return h('div', { innerHTML: HTMLString })
    }
  }

  function closeModal(el: HTMLElement) {
    render(null, el)
    activeModal.value = null
  }

  function closeActiveModal() {
    if (activeModal.value != null) {
      render(null, activeModal.value.el)
      activeModal.value = null
    }
  }

  function createComponent(
    component: Component,
    props?: SlotProps,
    slots?: SlotChildren
  ): VNode {
    return h(component, props, slots)
  }

  return {
    createSlotGeneric,
    createSlot,
    generateModal,
    showSelectedModal,
    showReplacementModal,
    createComponent,
    HTMLtoComponent,
    closeModal,
    closeActiveModal,
    deleteSelectedModal,
    hasActiveModal
  }
}
